import React, { useEffect, useRef, useState } from 'react'
import { Alert, Button, Modal, Space } from 'antd'
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  RightOutlined,
  FileTextOutlined,
  SafetyOutlined,
  ShoppingCartOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import fetchAPI from '../../../../lib/utils/fetch-api'
import { ReviewDocument, matchFor, isCatalogMatch } from './result-model'

export type TransferIssue = {
  code: string
  message: string
  line?: number
  field?: string
  action: 'orders' | 'requests' | 'line' | 'header' | 'review' | 'original'
}
type Readiness = { version: number; ready: boolean; issues: TransferIssue[] }
const labels: Record<string, string> = {
  unit: 'Единицы измерения',
  match: 'Позиция заказа',
  supplier_tax_id: 'ИНН поставщика',
  buyer_tax_id: 'ИНН покупателя',
  total: 'Итог с НДС',
  document_date: 'Дата накладной',
}
export function useOnecReadiness(reviewId: string, document: ReviewDocument, enabled: boolean) {
  const [data, setData] = useState<Readiness | null>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [sourceIssue, setSourceIssue] = useState<TransferIssue | null>(null)
  const generation = useRef(0)
  const path = `/api/supply/reviews/${reviewId}/documents/${document.id}/onec-write/readiness`
  const refresh = async () => {
    const request = ++generation.current
    setLoading(true)
    setError('')
    try {
      const result = await fetchAPI(path)
      if (result.error || !result.data || result.data.version !== document.version)
        throw new Error(
          result.error?.message ||
            result.error ||
            'Накладная изменилась. Обновите её перед отправкой',
        )
      if (generation.current !== request) return null
      setData(result.data)
      return result.data as Readiness
    } catch (cause: any) {
      if (generation.current === request) {
        setData(null)
        setError(cause.message || 'Не удалось проверить готовность к отправке')
      }
      return null
    } finally {
      if (generation.current === request) setLoading(false)
    }
  }
  useEffect(() => {
    setData(null)
    setError('')
    setOpen(false)
    setSourceIssue(null)
    if (enabled) void refresh()
    return () => {
      generation.current++
    }
  }, [path, document.version, document.status, enabled])
  const ensure = async () => {
    setSourceIssue(null)
    const current = await refresh()
    if (!current?.ready) setOpen(true)
    return !!current?.ready
  }
  const report = (message: string) => {
    const line = /Строка (\d+):/i.exec(message)
    setSourceIssue({
      code: 'source',
      message,
      ...(line ? { line: Number(line[1]) } : {}),
      action: line ? 'line' : 'review',
    })
    setOpen(true)
  }
  return { document, data, error, loading, open, setOpen, refresh, ensure, report, sourceIssue }
}
export function OnecReadiness({
  state,
  dirty,
  onResolve,
  compact = false,
  onCreateOrder,
  createdSupplierOrder,
}: {
  compact?: boolean
  state: ReturnType<typeof useOnecReadiness>
  dirty: boolean
  onResolve?: (issue: TransferIssue) => void
  onCreateOrder?: () => void
  createdSupplierOrder?: { number: string }
}) {
  const { document, data, error, loading, open, setOpen, refresh } = state
  const issues = [...(data?.issues || []), ...(state.sourceIssue ? [state.sourceIssue] : [])]
  const blocking = issues.filter(issue => issue.code !== 'review')
  const binding = (issue: TransferIssue) => {
    const line = document.original.items?.find(item => item.line_number === issue.line)
    const row = document.draft.items.find(item => item.line_number === issue.line)
    return (issue.code === 'order_line' && row?.allocations?.find(part => part.match.dataset === 'requests')?.match) || (line && matchFor(line, row))
  }
  const canFindRequest = (issue: TransferIssue) => {
    const match = binding(issue)
    return issue.code === 'order_line' && (!match || isCatalogMatch(match) || !['requests', 'orders'].includes(match.dataset))
  }
  const requestIssues = issues.filter(issue => {
    const match = binding(issue)
    return issue.code === 'order_line' && match && !isCatalogMatch(match) && match.dataset === 'requests'
  })
  const requestNumbers = [...new Set(requestIssues.map(issue => {
    const match = binding(issue)
    return match && !isCatalogMatch(match) ? match.document_number : ''
  }))]
  return (
    <>
      <Button
        size="small"
        title="Проверить готовность к созданию приходной в 1С"
        aria-label="Проверить готовность к 1С"
        type="text"
        disabled={dirty}
        loading={loading}
        icon={blocking.length || error ? <ExclamationCircleOutlined /> : <CheckCircleOutlined />}
        onClick={() => setOpen(true)}
      >
        {!compact && (error
          ? 'Проверить готовность к 1С'
          : blocking.length
          ? `Для отправки: ${blocking.length} ${blocking.length === 1 ? 'вопрос' : 'вопросов'}`
          : data?.ready
          ? 'Готово к отправке'
          : 'Подготовка к 1С')}
      </Button>
      <Modal
        title="Подготовка накладной к отправке в 1С"
        open={open}
        onCancel={() => setOpen(false)}
        width={840}
        centered
        className="supply-preparation"
        footer={
          <Space wrap>
            <Button
              icon={<ReloadOutlined />}
              loading={loading}
              disabled={dirty}
              onClick={() => {
                void refresh()
              }}
            >
              Проверить снова
            </Button>
            <Button onClick={() => setOpen(false)}>Закрыть</Button>
          </Space>
        }
      >
        <div className="supply-preparation__context"><FileTextOutlined /> Накладная № {document.draft.header.document_number || document.original.document_number || '—'}<span>·</span>{document.draft.header.supplier || document.original.supplier}</div>
        {error && <Alert showIcon type="error" message={error} />}
        {createdSupplierOrder && <Alert type="success" showIcon message={`Заказ № ${createdSupplierOrder.number} создан без проведения`} description="Проведите заказ в 1С и проверьте обновлённые строки накладной. Приходная накладная ещё не создана." style={{ marginBottom: 16 }} />}
        {!!requestIssues.length && onCreateOrder && <div className="supply-preparation__order-option">
          <ShoppingCartOutlined />
          <div><strong>Создать заказ по заявке</strong><p>Заявки: {requestNumbers.join(', ')}. Проверим существующие заказы и остаток. Состав и количество вы подтвердите перед созданием.</p></div>
          <Button type="primary" icon={<ShoppingCartOutlined />} disabled={dirty || loading} onClick={() => { setOpen(false); onCreateOrder() }}>Создать заказ по заявке</Button>
        </div>}
        {!!issues.length && (
          <section aria-label="Вопросы перед созданием в 1С">
            <div className="supply-preparation__intro">
              <span className="supply-preparation__status-icon"><ExclamationCircleOutlined /></span>
              <div><h3>Уточните данные перед созданием в 1С</h3><p>Откройте нужный пункт, внесите изменения и проверьте готовность снова.</p></div>
            </div>
            <ul className="supply-preparation__list">
            {issues.map((issue, index) => (
              <li
                className="supply-preparation__row"
                key={`${issue.code}:${issue.line}:${index}`}
              >
                <span className="supply-preparation__line-number" aria-hidden="true">{issue.line || <FileTextOutlined />}</span>
                <div className="supply-preparation__product">
                  <strong>{issue.line ? document.original.items?.find(line => line.line_number === issue.line)?.name || `Строка ${issue.line}` : issue.field && labels[issue.field] ? labels[issue.field] : 'Проверка накладной'}</strong>
                  {issue.line && <div><span>Строка {issue.line}</span>{issue.field && labels[issue.field] && <><span>·</span><span>{labels[issue.field]}</span></>}</div>}
                  <p>{issue.message}</p>
                </div>
                {onResolve && <div className="supply-preparation__row-actions">
                  <Button
                    type={index === 0 ? 'primary' : 'default'}
                    disabled={dirty || loading}
                    onClick={() => {
                      setOpen(false)
                      onResolve(issue)
                    }}
                  >
                    {issue.action === 'original'
                      ? 'Сверить с оригиналом'
                      : issue.action === 'orders'
                      ? 'Выбрать позицию заказа'
                      : issue.action === 'review'
                      ? 'Перейти к проверке'
                      : issue.action === 'header'
                      ? 'Исправить реквизиты'
                      : 'Исправить строку'} <RightOutlined />
                  </Button>
                  {canFindRequest(issue) && <Button size="small" type="link" icon={<SearchOutlined />} disabled={dirty || loading} onClick={() => { setOpen(false); onResolve({ ...issue, action: 'requests' }) }}>Найти заявку</Button>}
                </div>}
              </li>
            ))}
            </ul>
            <p className="supply-preparation__preserve-note"><SafetyOutlined /> Перед созданием ERP дополнительно сверит данные и остатки в 1С.</p>
          </section>
        )}
        {!error && data?.ready && !state.sourceIssue && (
          <Alert
            showIcon
            type="success"
            message="Данные накладной готовы к отправке"
            description="Нажмите «Создать в 1С» и проверьте подготовленный документ в окне подтверждения."
          />
        )}
      </Modal>
    </>
  )
}
